import { useState, useEffect } from 'react';
import { makeStyles, Typography, Link } from '@material-ui/core';
import {
  useSidebarOpenState,
  SidebarDivider,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

const GITHUB_REPO_URL = 'https://github.com/easyparkgroup/backstage';

interface VersionInfo {
  gitSha: string;
  gitBranch: string;
  prNumber: string;
  commitMessage: string;
  buildTimestamp: string;
}

const HIDDEN_BRANCHES = new Set(['master', 'main', 'local', 'unknown']);

const useStyles = makeStyles(theme => ({
  root: {
    padding: '6px 24px',
    overflow: 'hidden',
  },
  branchText: {
    color: theme.palette.navigation?.color ?? '#b5b5b5',
    fontSize: '0.7rem',
    lineHeight: 1.4,
    wordBreak: 'break-all',
    '&:hover': {
      color: theme.palette.navigation?.selectedColor ?? '#fff',
    },
  },
  prText: {
    color: theme.palette.navigation?.selectedColor ?? '#fff',
    fontWeight: 600,
    fontSize: '0.75rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export const DeploymentInfo = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [info, setInfo] = useState<VersionInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    discoveryApi
      .getBaseUrl('version-info')
      .then(baseUrl => {
        void fetchApi.fetch(`${baseUrl}/cookie`).catch(() => {});
        return fetchApi.fetch(baseUrl);
      })
      .then(res => res.json())
      .then(data => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  if (!info || HIDDEN_BRANCHES.has(info.gitBranch)) {
    return null;
  }

  if (!isOpen) {
    return null;
  }

  const branchUrl = `${GITHUB_REPO_URL}/tree/${encodeURIComponent(info.gitBranch)}`;
  const prUrl = info.prNumber
    ? `${GITHUB_REPO_URL}/pull/${info.prNumber}`
    : undefined;

  return (
    <>
      <SidebarDivider />
      <div className={classes.root}>
        <Link
          href={branchUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
        >
          <Typography className={classes.branchText}>
            {info.gitBranch}
          </Typography>
        </Link>
        {info.prNumber && prUrl && (
          <Link
            href={prUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
          >
            <Typography className={classes.prText}>
              PR #{info.prNumber}
            </Typography>
          </Link>
        )}
      </div>
    </>
  );
};
